import React, { useMemo } from 'react'
import { Box, Group, Modal, Title, Stepper, Space, Stack, UnstyledButton, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useStepperContext } from './StepperProvider'
import { Icon } from '@/components'

interface StepperModalProps {
  title: string
  amount?: React.ReactElement | string
  children: React.ReactNode
}

// @TODO: In the future, consider not using the Stepper form anymore.
// Our edge cases make it difficult to use as it's not very flexible.
const StepperModal: React.FC<StepperModalProps> = ({ title, amount, children }) => {
  const theme = useMantineTheme()

  const isMediumScreen = useMediaQuery(`(min-width: ${theme.breakpoints.md}px)`, true)

  const {
    activeStep,
    stepKey,
    steps,
    warning,
    error,
    isOpen,
    isLoading,
    isPostError,
    isOnGoing,
    isComplete,
    forceClose,
    close
  } = useStepperContext()

  const stepperActiveStep = useMemo(() => {
    if (isComplete) return steps.length + 1
    if (isOnGoing) return activeStep
    return -1
  }, [steps, activeStep, isOnGoing, isComplete])

  const activeStepNode = useMemo(() => {
    return React.Children.toArray(children).find((_, i) => {
      return i === activeStep
    })
  }, [children, activeStep])

  return (
    <Modal
      opened={isOpen}
      onClose={close}
      size={isMediumScreen ? theme.other.stepperModal.bodyWidth : '100%'}
      padding={0}
      radius={isMediumScreen ? 'lg' : 0}
      withCloseButton={false}
      styles={() => ({
        // full screen styling
        inner: { ...(!isMediumScreen && { padding: 0 }) },
        modal: {
          ...(!isMediumScreen && { height: '100%' })
        }
      })}>
      <Group
        spacing={0}
        align="stretch"
        sx={(t) => ({
          backgroundColor: t.white,
          borderRadius: isMediumScreen ? t.radius.lg : 0
        })}>
        <Box
          sx={(t) => ({
            padding: isMediumScreen ? t.spacing.lg : t.spacing.md,
            width: isMediumScreen ? t.other.stepperModal.wizardWidth : '100%',
            background: t.colors.gray[0],
            borderTopLeftRadius: isMediumScreen ? t.radius.lg : 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: isMediumScreen ? t.radius.lg : 0,
            borderBottomRightRadius: 0
          })}>
          <Title order={6} sx={(t) => ({ color: t.colors.gray[7], fontWeight: 600, textTransform: 'uppercase' })}>
            {title}
          </Title>

          {Boolean(amount) && (
            <Title order={3} sx={(t) => ({ color: t.colors.gray[9] })}>
              {amount}
            </Title>
          )}

          <Space h="lg" />

          <Stepper active={stepperActiveStep} color="cyan" orientation="vertical">
            {steps.map((step, i) => {
              const isStepActive = i === activeStep

              const getColor = () => {
                if (isStepActive && warning) return 'yellow'
                // This is not standard mantine color (nor is it registered in our theme file),
                // but our custom styles in the same theme file will take care of this entirely.
                if (isStepActive && isPostError) return 'post-error-yellow'
                // Our theme file will take care of this entirely.
                if (isStepActive && error) return 'red'
                return 'cyan'
              }

              const progressIcon =
                !warning && error ? (
                  <Icon name="cross" />
                ) : isStepActive && isPostError ? (
                  <Box mt={-1} /* Occular center */>
                    <Icon name="warningTriangle" size="xl" />
                  </Box>
                ) : undefined

              return (
                <Stepper.Step
                  description={step}
                  loading={isStepActive && !error && (isLoading || warning)}
                  progressIcon={progressIcon}
                  color={getColor()}
                  key={i}
                />
              )
            })}
          </Stepper>
        </Box>

        <Stack
          spacing={0}
          justify="center"
          sx={(t) => ({
            position: 'relative',
            padding: isMediumScreen ? t.other.stepperModal.contentPaddingDesktop : t.spacing.md,
            paddingRight: isMediumScreen ? t.other.stepperModal.contentPaddingRightDesktop : t.spacing.md,
            width: t.other.stepperModal.contentWidth
          })}>
          <React.Fragment key={stepKey}>{activeStepNode}</React.Fragment>
        </Stack>
      </Group>

      <UnstyledButton
        sx={{ position: 'absolute', top: theme.spacing.xl, right: theme.spacing.xl }}
        onClick={forceClose}>
        <Icon name="cross" sx={(t) => ({ color: t.colors.gray[8] })} />
      </UnstyledButton>
    </Modal>
  )
}

export { StepperModal }
